// featureinfo.js

import Overlay from 'ol/Overlay.js';
import { getMap } from './mapInstance.js';
import { Style, Stroke, Fill, Text } from 'ol/style.js';
import VectorLayer from 'ol/layer/Vector';
import { setCommand, setStatus } from './mapUtils.js';
import { resetActiveCommandsOnly } from './resetApplication.js';
import { setActiveSidebarTool, setSidebarToolActive } from './dialogs.js';
import { translate } from './languageTranslator.js';


let infoActive = false;
let infoOverlay = null;
let lastHighlightedFeature = null;
let escListener = null;
let infoToolsInitialized = false;
let observerInitialized = false;


const highlightStyle = new Style({
  stroke: new Stroke({ color: '#ff8800', width: 3 }),
  fill: new Fill({ color: 'rgba(255,136,0,0.25)' }),
  text: new Text({ font: '12px sans-serif', fill: new Fill({ color: '#880' }) })
});

function getFeatureHighlightStyle(feature, layer, map) {
  if (!layer?.get?.('poiSetId')) return highlightStyle;

  const poiFeatureInfoStyle = layer.get('poiFeatureInfoStyle');
  if (poiFeatureInfoStyle) return poiFeatureInfoStyle;

  const resolution = map?.getView?.()?.getResolution?.();
  const renderedStyle = layer.getStyleFunction?.()?.(feature, resolution);
  const originalStyles = Array.isArray(renderedStyle)
    ? renderedStyle.filter(Boolean)
    : [renderedStyle].filter(Boolean);

  return originalStyles;
}

const INFO_FIELD_DEFINITIONS = [
  {
    keys: ['huisnummer', 'u104720huisnummer'],
    labelKey: 'feature_info.field.huisnummer'
  },
  {
    keys: ['openbare_ruimte', 'u104719openbare_ruimte'],
    labelKey: 'feature_info.field.openbare_ruimte'
  },
  {
    keys: ['gebruiksdoel', 'gebruiksdeol', 'u104718gebruiksdoel'],
    labelKey: 'feature_info.field.gebruiksdoel'
  },
  {
    keys: ['huisletter', 'u104721huisletter'],
    labelKey: 'feature_info.field.huisletter'
  },
  {
    keys: ['toevoeging', 'u104722toevoeging'],
    labelKey: 'feature_info.field.toevoeging'
  },
  {
    keys: ['postcode6_BAG', 'u104723postcode6_BAG'],
    labelKey: 'feature_info.field.postcode6_bag'
  },
  {
    keys: ['u108213Admin_name', 'u108213Admin_naam', 'name'],
    labelKey: 'feature_info.field.name'
  },
  {
    keys: ['u108212Aantal_VBO_BAG'],
    labelKey: 'feature_info.field.number_of_addresses'
  }
];

const BELGIUM_PC5_TYPE_NAME = 'GEOLINQ:BE_STATISTICAL_SECTORS';
const BELGIUM_POSTAL_CODE_TYPE_NAME = 'GEOLINQ:BE_4PP';
const NETHERLANDS_PC4_TYPE_NAME = 'GEOLINQ:NL_4PP';

const BELGIUM_POSTAL_CODE_INFO_FIELDS = [
  {
    keys: ['nouveau_po', 'bd_code', 'postcode', 'postal_code'],
    labelKey: 'feature_info.field.postcode6_bag'
  },
  {
    keys: ['sector_name'],
    labelKey: 'feature_info.field.name'
  },
  {
    keys: ['box_brut_bd'],
    label: 'Bruto BD'
  },
  {
    keys: ['box_net_bd'],
    label: 'Netto BD'
  },
  {
    keys: ['categorie_bd'],
    label: 'Categorie BD'
  }
];

const NETHERLANDS_PC4_DISTRIBUTOR_FIELDS = [
  {
    keys: ['bruto_vsp', 'bruto_VSP'],
    label: 'Bruto VSP'
  },
  {
    keys: ['netto_vsp', 'netto_VSP'],
    label: 'Netto VSP'
  },
  {
    keys: ['aantal_weekblad'],
    label: 'Aantal Weekblad'
  },
  {
    keys: ['bruto_weekblad'],
    label: 'Bruto Weekblad'
  }
];

const NETHERLANDS_PC4_INFO_FIELDS = [
  {
    keys: ['u108213Admin_name', 'u108213Admin_naam', 'name'],
    labelKey: 'feature_info.field.name'
  },
  {
    keys: ['u108212Aantal_VBO_BAG'],
    labelKey: 'feature_info.field.number_of_addresses'
  },
  ...NETHERLANDS_PC4_DISTRIBUTOR_FIELDS
];

function isBelgiumPC5Layer(layer) {
  const title = String(layer?.get?.('title') || '').trim().toUpperCase();
  const typeName = String(layer?.get?.('typeName') || '').trim().toUpperCase();

  return title === 'PC5' && typeName === BELGIUM_PC5_TYPE_NAME;
}

function isBelgiumPostalCodeLayer(layer) {
  const title = String(layer?.get?.('title') || '').replace(/[^a-z0-9]/gi, '').toUpperCase();
  const typeName = String(layer?.get?.('typeName') || '').trim().toUpperCase();

  return title === 'POSTALCODE'
    || title === 'POSTALCODES'
    || typeName === BELGIUM_POSTAL_CODE_TYPE_NAME
    || typeName.endsWith(':U1014134VIEWGROUPADRESTYPE1');
}

function isNetherlandsPC4Layer(layer) {
  const title = String(layer?.get?.('title') || '').trim().toUpperCase();
  const typeName = String(layer?.get?.('typeName') || '').trim().toUpperCase();

  return title === 'PC4' || typeName === NETHERLANDS_PC4_TYPE_NAME;
}

function isPostalAttributesLayer(layer) {
  const role = layer?.get?.('semanticRole') || layer?.get?.('addressAidsRole');
  const title = String(layer?.get?.('title') || '').trim().toUpperCase();
  const typeName = String(layer?.get?.('typeName') || '').trim().toLowerCase();
  return ['pc4', 'pc6'].includes(role)
    || ['PC4', 'PC6'].includes(title)
    || ['geolinq:nl_pc6', 'geolinq:nl_4pp', 'geolinq:nl_6pp'].includes(typeName);
}

function renderAllProperties(props, geometryName = 'geometry') {
  return Object.entries(props)
    .filter(([key, value]) => key !== 'geometry' && key !== geometryName && !value?.getType?.())
    .map(([key, value]) => `
      <div class="info-row">
        <span class="key">${escapeHtml(key)}</span>
        <span class="value">${escapeHtml(value !== null && typeof value === 'object' ? JSON.stringify(value) : value)}</span>
      </div>
    `)
    .join('');
}

function isBelgiumSelected() {
  return String(document.getElementById('country-select')?.value || '') === '4';
}

function getBelgiumLanguageSuffix() {
  const option = document.getElementById('language-select')?.selectedOptions?.[0];
  const language = [option?.dataset?.abbreviation, option?.dataset?.name, option?.textContent]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/\b(fr|french|fran[cç]ais)\b/.test(language)) return 'fr';
  if (/\b(de|ger|german|deutsch)\b/.test(language)) return 'de';
  return 'nl';
}

function getLocalizedBelgiumKeys(keysByLanguage) {
  const suffix = getBelgiumLanguageSuffix();
  const selectedKey = keysByLanguage[suffix];

  return [selectedKey, ...Object.values(keysByLanguage).filter(key => key !== selectedKey)]
    .filter(Boolean);
}

function isAddressInfoLayer(layer) {
  const typeName = String(layer?.get?.('typeName') || '').trim().toLowerCase();
  return layer?.get?.('semanticRole') === 'address'
    || layer?.get?.('addressAidsRole') === 'address'
    || ['geolinq:nl_full_address', 'geolinq:be_full_address'].includes(typeName);
}

function getBelgiumInfoFieldDefinitions(layer) {
  if (!isAddressInfoLayer(layer)) {
    return [
      {
        keys: getLocalizedBelgiumKeys({
          nl: 'u108267Admin_NL',
          fr: 'u108266Admin_FR',
          de: 'u108265Admin_GER'
        }),
        labelKey: 'feature_info.field.name'
      },
      {
        keys: ['u108264Aantal_adres', 'u108268Aantal_adres_bepost'],
        labelKey: 'feature_info.field.number_of_addresses'
      }
    ];
  }

  return [
    {
      keys: ['u107255house_number', 'house_number'],
      labelKey: 'feature_info.field.huisnummer'
    },
    {
      keys: getLocalizedBelgiumKeys({
        nl: 'u107266streetname_nl',
        fr: 'u107265streetname_fr',
        de: 'u107264streetname_de'
      }),
      labelKey: 'feature_info.field.openbare_ruimte'
    },
    {
      keys: ['u107260postcode', 'postcode'],
      labelKey: 'feature_info.field.postcode6_bag'
    }
  ];
}

// Inject CSS for info popup
(function injectStyles(){
  const css = `
    #info-popup { position: absolute; z-index: 2000; background: none!important; border: none;
      padding: 8px; font-family: sans-serif; font-size: .9rem; display: flex;
      flex-direction: column; gap: 4px; pointer-events: none;
      visibility: hidden; white-space: normal; word-wrap: break-word; align-items: stretch;
    }
    #info-popup .info-row { display: grid; grid-template-columns: max-content auto; gap: 2px 8px; align-items: start; }
    #info-popup .info-row.info-values-only { grid-template-columns: auto; }
    #info-popup .info-address-line { display: flex; flex-wrap: nowrap; gap: 0.75em; align-items: baseline; white-space: nowrap; }
    #info-popup .info-address-line > .info-row { flex-shrink: 0; }
    #info-popup .info-address-line span.value { white-space: nowrap; overflow-wrap: normal; word-break: normal; }
    #info-popup span.key { color: red; font-weight:600; text-align:left; }
    #info-popup span.key::after { content: ":"; margin-left:2px; }
    #info-popup span.value { color: blue; text-align:left; }
    .info-button.active { background-color:#0d6efd!important; color:#fff!important; }
  `;
  const style = document.createElement('style');
  style.appendChild(document.createTextNode(css));
  document.head.appendChild(style);
})();

export function observeInfoButtonAndInit() {
  if (observerInitialized) return;
  observerInitialized = true;

  const sidebar = document.getElementById('sidebar');
  if (!sidebar) {
    console.error('Sidebar not found');
    return;
  }

  const existingBtn = sidebar.querySelector('.info-button');
  if (existingBtn) {
    setupInfoTools();
    return;
  }

  const observer = new MutationObserver(() => {
    const btn = sidebar.querySelector('.info-button');
    if (!btn) return;

    observer.disconnect();
    setupInfoTools();
  });

  observer.observe(sidebar, { childList: true, subtree: true });
}


function ensureOverlay() {

  const map = getMap();
  if (!map) return;

  if (!infoOverlay) {
    let elt = document.getElementById('info-popup');
    if (!elt) {
      elt = document.createElement('div');
      elt.id = 'info-popup';
      elt.style.visibility = 'hidden';
      document.body.appendChild(elt);
    }
    infoOverlay = new Overlay({
      element: elt,
      autoPan: false,
      positioning: 'bottom-left',
      stopEvent: false,
      autoPanAnimation: { duration: 250 }
    });
    map.addOverlay(infoOverlay);
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function getInfoFieldLabel(field, layer) {
  return field.label || translate(field.labelKey);
}

const SHOW_CURATED_ATTRIBUTE_NAMES = false;

function renderCuratedInfoValue(name, value) {
  const rowClass = SHOW_CURATED_ATTRIBUTE_NAMES ? 'info-row' : 'info-row info-values-only';
  const label = SHOW_CURATED_ATTRIBUTE_NAMES ? `<span class="key">${escapeHtml(name)}</span>` : '';
  return `<div class="${rowClass}">${label}<span class="value">${escapeHtml(value ?? '—')}</span></div>`;
}

function renderFeatureInfo(feature, layer) {
  const props = feature?.getProperties?.() || {};
  const typeName = String(layer?.get?.('typeName') || '').trim().toLowerCase();
  const title = String(layer?.get?.('title') || '').trim().toLowerCase();
  if (typeName === 'geolinq:nl_6pp'
    || (!isBelgiumSelected() && !typeName && title === 'pc6')) {
    const key = Object.keys(props).find(key => key.toLowerCase() === '6pp');
    return renderCuratedInfoValue('6pp', props[key]);
  }
  if (typeName === 'geolinq:nl_full_address'
    || (!isBelgiumSelected() && !typeName && ['address', 'addresses'].includes(title))) {
    const keys = new Map(Object.keys(props).map(key => [key.toLowerCase(), key]));
    return [
      ['Straatnaam', 'Huisnummer', 'HuisnummerToevoeging'],
      ['nl_6pp', 'Plaatsnaam']
    ].map(fields => `<div class="info-address-line">${fields.map(key => {
      const value = props[keys.get(key.toLowerCase())];
      // Preserve every column, including an empty house-number addition.
      return renderCuratedInfoValue(key, String(value ?? '').replace(/\s+/g, ' ').trim());
    }).join('')}</div>`).join('');
  }
  if (typeName === 'geolinq:nl_4pp') {
    const keys = new Map(Object.keys(props).map(key => [key.toLowerCase(), key]));
    const field = ['4pp', 'postcode4', 'pc4', 'u104987pc4_cbs', 'postcode'].find(key => keys.has(key));
    return renderCuratedInfoValue('PC4', field ? props[keys.get(field)] : '');
  }
  if (typeName === 'geolinq:nl_pc6'
    || (!isBelgiumSelected() && !typeName && title === 'pc4')) {
    const key = Object.keys(props).find(key => key.toLowerCase() === 'postcode6');
    return renderCuratedInfoValue('postcode6', props[key]);
  }
  if (typeName === 'geolinq:nl_buurten'
    || (!isBelgiumSelected() && !typeName && ['neighbourhoods', 'neighborhoods'].includes(title))) {
    const key = Object.keys(props).find(key => key.toLowerCase() === 'buurtnaam');
    return renderCuratedInfoValue('buurtnaam', props[key]);
  }
  if (typeName === 'geolinq:nl_wijken'
    || (!isBelgiumSelected() && !typeName && title === 'districts')) {
    const key = Object.keys(props).find(key => key.toLowerCase() === 'wijknaam');
    return renderCuratedInfoValue('wijknaam', props[key]);
  }
  if (typeName === 'geolinq:nl_gemeenten'
    || (!isBelgiumSelected() && !typeName && title === 'municipalities')) {
    const key = Object.keys(props).find(key => key.toLowerCase() === 'gemeentenaam');
    return renderCuratedInfoValue('gemeentenaam', props[key]);
  }
  if (['geolinq:nl_landgebied', 'geolinq:nl_provinciegebied'].includes(typeName)
    || (!isBelgiumSelected() && !typeName && ['countries', 'provinces'].includes(title))) {
    const key = Object.keys(props).find(key => key.toLowerCase() === 'naam');
    return renderCuratedInfoValue('naam', props[key]);
  }
  if (typeName === 'geolinq:be_full_address'
    || (isBelgiumSelected() && !typeName && ['address', 'addresses'].includes(title))) {
    const keys = new Map(Object.keys(props).map(key => [key.toLowerCase(), key]));
    return [
      ['straatnaam', 'huisnunmmer', 'huisnummertoevoeging'],
      ['4pp', 'plaats']
    ].map(fields => `<div class="info-address-line">${fields.map(key => {
      const value = props[keys.get(key)];
      return renderCuratedInfoValue(key, String(value ?? '').replace(/\s+/g, ' ').trim());
    }).join('')}</div>`).join('');
  }
  if (typeName === 'geolinq:be_statistical_sectors'
    || (isBelgiumSelected() && !typeName && title === 'nis9')) {
    const key = Object.keys(props).find(key => key.toLowerCase() === 'niscode');
    return renderCuratedInfoValue('niscode', props[key]);
  }
  if (typeName === 'geolinq:be_4pp'
    || (isBelgiumSelected() && !typeName && ['postalcode', 'postalcodes'].includes(title))) {
    const key = Object.keys(props).find(key => key.toLowerCase() === '4pp');
    return renderCuratedInfoValue('4pp', props[key]);
  }
  if (['geolinq:be_country', 'geolinq:be_regions', 'geolinq:be_provinces', 'geolinq:be_arrondissements', 'geolinq:be_municipalities', 'geolinq:be_deelgemeenten'].includes(typeName)
    || (isBelgiumSelected() && !typeName && ['countries', 'regions', 'provincies', 'provinces', 'borough', 'municipalities', 'sub municipalities', 'nis6'].includes(title))) {
    const keys = new Map(Object.keys(props).map(key => [key.toLowerCase(), key]));
    return ['nameger', 'namefre', 'namedut'].map(key => {
      const value = props[keys.get(key)];
      return renderCuratedInfoValue(key, value);
    }).join('');
  }
  // The original Info execution and curated fields remain in docs/backups/
  // feature-info-before-all-address-attributes/featureinfo.js for restoration.
  const layerName = layer?.get?.('typeName') || layer?.get?.('title') || '';
  return `<div class="info-row"><span class="key">Layer</span><span class="value">${escapeHtml(layerName)}</span></div>`
    + renderAllProperties(props, feature?.getGeometryName?.());
}

function displayFeatureInfo(evt) {
  const map = getMap();
  if (!map) return;

  ensureOverlay();
  const elt = infoOverlay.getElement();
  if (!elt) return;

  const hits = [];
  map.forEachFeatureAtPixel(
    evt.pixel,
    (feature, layer) => {
      hits.push({ feature, layer });
      return undefined;
    },
    {
      layerFilter: layer =>
        (layer instanceof VectorLayer || typeof layer?.getSource === 'function') &&
        layer.getVisible?.() &&
        !layer.get?.('disabledByRights'),
      hitTolerance: 8
    }
  );
  // Address attributes should take priority over overlapping postcode polygons.
  const addressHits = hits.filter(({ layer }) => isAddressInfoLayer(layer));
  const selectedHits = addressHits.length ? addressHits : hits;
  const hit = selectedHits[0] || null;
  const feat = hit?.feature || null;

  if (lastHighlightedFeature && lastHighlightedFeature !== feat) {
    lastHighlightedFeature.setStyle(undefined);
  }
  lastHighlightedFeature = feat;

  if (!feat) {
    infoOverlay.setPosition(undefined);
    elt.style.visibility = 'hidden';
    return;
  }

  feat.setStyle(getFeatureHighlightStyle(feat, hit?.layer, map));

  // Report one feature per click in every country. Selection overlays and
  // overlapping layers can return multiple hits for the same location.
  const visibleHits = selectedHits.slice(0, 1);
  elt.innerHTML = visibleHits
    .map(({ feature, layer }) => renderFeatureInfo(feature, layer))
    .join('');

  infoOverlay.setPosition(evt.coordinate);
  elt.style.visibility = 'visible';
}


function deactivateInfoMode() {
  const map = getMap();
  if (!map) return;

  infoActive = false;

  document.querySelector('.info-button')?.classList.remove('active');
  setSidebarToolActive('.info-button', false);
  map.getViewport().style.cursor = '';

  map.un('pointermove', pointerMoveHandler);
  map.un('click', displayFeatureInfo);

  if (infoOverlay) {
    infoOverlay.setPosition(undefined);
    infoOverlay.getElement().style.visibility = 'hidden';
  }

  if (lastHighlightedFeature) {
    lastHighlightedFeature.setStyle(undefined);
    lastHighlightedFeature = null;
  }

  if (escListener) {
    document.removeEventListener('keydown', escListener);
    escListener = null;
  }

}

document.addEventListener('application-reset-active-tools', () => {
  deactivateInfoMode();
});


function pointerMoveHandler(evt) {

    const map = getMap();
  if (!map) return;

  if (evt.dragging) return;
  const over = map.hasFeatureAtPixel(evt.pixel);
  map.getViewport().style.cursor = over ? 'help' : '';
}

export function setupInfoTools() {
  if (infoToolsInitialized) {
    return;
  }

  const map = getMap();
  if (!map) {
    return;
  }

  let btn = document.querySelector('.info-button');
  if (!btn) {
    console.warn('No .info-button element found');
    return;
  }

  infoToolsInitialized = true;

  const cleanBtn = btn.cloneNode(true);
  btn.parentNode.replaceChild(cleanBtn, btn);
  btn = cleanBtn;

  btn.addEventListener('click', () => {

  if (!infoActive) {
  resetActiveCommandsOnly({ preserveAddressAidsResults: true });
  infoActive = true;
  setActiveSidebarTool(btn, { lock: true });

  map.on('click', displayFeatureInfo);
  setCommand('Click on the feature to view feature info, ESC to cancel');
  setStatus('Feature Info activated');

  // ADD ESC LISTENER HERE
  escListener = (evt) => {
    if (evt.key === 'Escape') {
      deactivateInfoMode();
    }
  };
  document.addEventListener('keydown', escListener);

  } 
  else 
  {
    deactivateInfoMode();
  }

  });
}

